import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { submitRateIt, trackTiriView } from 'services/tiri';
import RateItItemCard from './RateItItemCard';
import RateItItemSkeleton from './RateItItemSkeleton';
import RateItMilestoneModal from './RateItMilestoneModal';
import RateItPager from './RateItPager';
import RateItRatingToast from './RateItRatingToast';
import RateItReferralBanner from './RateItReferralBanner';
import ReferralAwardModal from 'screens/DeepLink/components/ReferralFlow/ReferralAwardModal';
import {
  RATE_IT_DEFAULT_MILESTONE_COUNT,
  RATE_IT_PHOTO_AUTO_ADVANCE_MS,
  RATE_IT_TOAST_MS
} from '../constants';

const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

/**
 * The pager, and the owner of the whole rating sequence.
 *
 * THE STRUCTURAL DECISION HERE, and the one thing to preserve in any edit: the rating is fired
 * from the FEED, not from the card. The brief requires that navigating never cancel a rating in
 * flight, and that is impossible to guarantee from the card, because a card can unmount the
 * moment the fan scrolls. Because `results`, the in-flight set and the request itself all live
 * here — at a component that survives the whole session — scrolling cannot touch them.
 *
 * Two web-specific reinforcements of the same point: `Api` passes no `AbortSignal`, so nothing
 * cancels the request on unmount (do not add one); and React 18's StrictMode double-invokes
 * effects in development, so any design that fired the request from a card effect would
 * double-submit every rating.
 *
 * THE LOCK RULE, mirroring the app: while a moment is on screen the fan can change their pick as
 * often as they like; once they scroll away from a moment they rated, it is locked (`lockedIds`),
 * even if they come back. A moment they skipped without rating stays rateable. So a rating never
 * advances the feed by itself — leaving is what locks, and leaving is the fan's call. The idle
 * auto-advance is held while the active moment is rated for the same reason.
 *
 * THERE IS NO SHARE AFFORDANCE ON WEB, deliberately. Mobile's every-5 celebration carries an
 * Invite Friends CTA; this surface is the other END of that invite — a referral landing — so
 * the celebration here keeps mobile's trophy and headline and drops the CTA with it. See
 * `RateItMilestoneModal`.
 *
 * The milestone is a CONFIRMATION, never a receipt — ON THIS SURFACE. That used to be true
 * everywhere and for a structural reason: `POST /tiri/rateit/submit` carried no reward figure of
 * any kind, so no client could show one. It now returns `pointsAwarded`, and the APP raises a
 * participation toast beside this modal. This surface deliberately does not, yet: it is a
 * referral landing whose job is to convert a visitor, and it has no toast of its own for a
 * points figure to live in. If that changes, the number is already on the response — see
 * `pointsAwarded` in `services/tiri.js`.
 *
 * The referral payout is the exception, and gets its own popup: the rating that completes an
 * invite comes back with `referralCompleted`, and `ReferralAwardModal` says what both fans
 * earned before the milestone celebration follows it (never beside it). Progress before then is read from
 * `GET /tiri/rateit/referral/status`.
 */
const RateItFeed = ({
  items,
  sharedRating,
  hasNextPage,
  isFetchingNextPage,
  loadMore,
  onBack,
  referral,
  /**
   * The referral link this landing was opened with, if any.
   *
   * Sent with every rating so the server knows which invite it belongs to — a fan holds one
   * referral per link, so without it a rating made here could not be told from one made under
   * any of their other invites.
   */
  shortCode,
  isSignedIn,
  onRequireAuth,
  pendingRating,
  onPendingRatingApplied
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const pagerRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  /** Keyed by item id, held above the cards so a rating survives its card unmounting. */
  const [results, setResults] = useState(() =>
    sharedRating && items[0] ? { [items[0]._id]: sharedRating } : {}
  );
  const resultsRef = useRef(results);
  resultsRef.current = results;
  /**
   * Moments whose rating can no longer change: rated, then scrolled away from — or rated before
   * this visit (the shared moment's `sharedRating`, a feed item's `myRating`), which is the same
   * thing.
   */
  const [lockedIds, setLockedIds] = useState(() =>
    sharedRating && items[0] ? { [items[0]._id]: true } : {}
  );
  const [toastRating, setToastRating] = useState(null);
  /**
   * Per-item tier counts that supersede `item.tallies`: the optimistic +1 written on click, then
   * the server's counts once the rating lands. Dropped on failure, so the card falls back to the
   * item's own counts.
   */
  const [tallies, setTallies] = useState({});
  /**
   * Starts muted and is turned on by the first rating — see `handleRate`.
   *
   * There is no toggle any more; the design gives the top-right corner to the TIRI lockup. So
   * this is one-way: muted until the fan's first gesture, then on for the session.
   */
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  /** `{ count }` while the every-5 celebration is up, null otherwise. */
  const [milestone, setMilestone] = useState(null);
  /**
   * `{ points, referrerName }` once a rating completes this fan's invite, until they dismiss
   * its popup. Shown ahead of `milestone`: the same fifth rating usually closes both.
   */
  const [referralAward, setReferralAward] = useState(null);

  /**
   * Items with a request in flight. A ref rather than state: nothing renders from it, and it must
   * be readable synchronously inside the click handler so a change made while one is in flight
   * joins it (see `sendRating`) rather than racing it.
   */
  const pendingRef = useRef(new Set());
  /** The newest pick per item — what `sendRating` converges the server to. */
  const latestRef = useRef({});
  /** What the server last confirmed per item — what a failed request rolls back to. */
  const confirmedRef = useRef(
    sharedRating && items[0] ? { [items[0]._id]: sharedRating } : {}
  );
  /** Items clicked in THIS visit, which seeding from `myRating` must not lock. */
  const ratedThisSessionRef = useRef(new Set());

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;
  const hasNextPageRef = useRef(hasNextPage);
  hasNextPageRef.current = hasNextPage;
  const isFetchingRef = useRef(isFetchingNextPage);
  isFetchingRef.current = isFetchingNextPage;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const toastTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    []
  );

  /**
   * Seeds `results` and the lock from each feed item's `myRating` — moments this fan rated
   * before, which the server keeps in the feed until their cycle pays out. Without this they
   * would show unrated and clickable, and a click would now CHANGE a rating the fan already left.
   */
  useEffect(() => {
    const seeded = {};
    const locked = {};
    items.forEach((item) => {
      if (item.myRating && !ratedThisSessionRef.current.has(item._id)) {
        seeded[item._id] = item.myRating;
        locked[item._id] = true;
        confirmedRef.current[item._id] = item.myRating;
      }
    });
    if (Object.keys(seeded).length === 0) return;
    // Existing entries win: this visit's own ratings are at least as fresh.
    setResults((prev) => ({ ...seeded, ...prev }));
    setLockedIds((prev) => ({ ...locked, ...prev }));
  }, [items]);

  /**
   * This session's own views still in flight, keyed by item id, added on top of the server's
   * `viewCount` so the visitor sees their view land the moment they reach a moment — as the app
   * does. Each view's response carries the server's count, which includes other fans' views since
   * the feed was fetched; it goes in `serverViews` and the in-flight +1 drops off. Held here, above
   * the cards, for the same reason `results` is: a card can unmount.
   */
  const [localViews, setLocalViews] = useState({});
  const [serverViews, setServerViews] = useState({});
  const bumpViews = useCallback((itemId, by) => {
    setLocalViews((prev) => ({ ...prev, [itemId]: (prev[itemId] ?? 0) + by }));
  }, []);

  const isSignedInRef = useRef(isSignedIn);
  isSignedInRef.current = isSignedIn;
  /**
   * The index last counted. StrictMode double-invokes effects in development, and without this
   * every page change would count twice there. A moment reached again by scrolling away and back
   * is a NEW index change, so it still counts — Reach counts every visibility, as on mobile.
   */
  const lastTrackedIndexRef = useRef(null);

  // Counts the active moment's view — and, server-side, logs its Reach — on mount and on every
  // page change. Signed-in only: `POST /tiri/view` needs a real user, as a Reach event does, so a
  // signed-out visitor's views are counted neither on the card nor in Insights.
  useEffect(() => {
    if (lastTrackedIndexRef.current === currentIndex) return;
    const item = itemsRef.current[currentIndex];
    if (!item || !isSignedInRef.current) return;
    lastTrackedIndexRef.current = currentIndex;

    bumpViews(item._id, 1);
    trackTiriView({ tiriId: item._id }).then((res) => {
      // `Api` never throws; a failure is simply a body with no `data`. Either way the optimistic
      // bump comes off — on success the server's count, which already includes it, replaces it.
      // Max, not last-write: counts only grow, and fast scrolling can land responses out of order.
      const viewCount = res?.data?.viewCount;
      if (typeof viewCount === 'number') {
        setServerViews((prev) => ({
          ...prev,
          [item._id]: Math.max(prev[item._id] ?? 0, viewCount)
        }));
      }
      bumpViews(item._id, -1);
    });
    // Keyed on the index alone: `items` grows as pages load, and depending on it would count a
    // view the visitor never made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  /**
   * The idle auto-advance, mirroring the app: a photo moves on after
   * `RATE_IT_PHOTO_AUTO_ADVANCE_MS`, a video when the clip ends (`RateItMedia` plays it once and
   * reports `onEnded`). Either way the fan can always scroll sooner.
   */
  const advanceIdle = useCallback(() => {
    const nextIndex = currentIndexRef.current + 1;
    if (nextIndex < itemsRef.current.length) {
      pagerRef.current?.goTo(nextIndex);
    } else if (hasNextPageRef.current && !isFetchingRef.current) {
      loadMoreRef.current();
    }
  }, []);

  // A celebration on screen holds the idle advance — it belongs on the moment that earned it.
  const isModalOpen = referralAward !== null || milestone !== null;
  const activeIsPhoto = !!items[currentIndex] && items[currentIndex].mediaType !== 'video';
  // So does a rated active moment: moving the fan on would lock their pick without them leaving.
  const activeIsRated = !!items[currentIndex] && !!results[items[currentIndex]._id];

  // (Re)start the photo timer whenever the active moment changes or a modal closes. A resume
  // restarts the full delay rather than what was left of it — same as the app.
  useEffect(() => {
    if (!activeIsPhoto || isModalOpen || activeIsRated) return undefined;
    const timer = setTimeout(advanceIdle, RATE_IT_PHOTO_AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
  }, [currentIndex, activeIsPhoto, isModalOpen, activeIsRated, advanceIdle]);

  const showToast = useCallback((rating) => {
    setToastRating(rating);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastRating(null), RATE_IT_TOAST_MS);
  }, []);

  /** The server's last counts per item — what a failed request puts the counter back to. */
  const serverTalliesRef = useRef({});

  /**
   * Sends the fan's newest pick for one item, and keeps sending until the server has it.
   *
   * One request per item at a time. A change made while one is in flight only updates
   * `latestRef`; when the request lands, the loop sees the server is behind and sends again. So
   * the server always ends on the fan's last pick, with no two requests for the same item racing.
   *
   * Milestones and referral awards can only come back on the FIRST rating of an item (the server
   * never counts a change), so each celebration fires at most once per item.
   */
  const sendRating = useCallback(
    async (itemId) => {
      pendingRef.current.add(itemId);
      try {
        let sent = latestRef.current[itemId];
        while (sent && sent !== confirmedRef.current[itemId]) {
          // eslint-disable-next-line no-await-in-loop
          const res = await submitRateIt({ tiriId: itemId, rating: sent, shortCode });

          // `Api` with `{silent: true}` resolves rather than throws, so a failure is a field on
          // the response, not a rejection.
          if (!res || res.err) {
            // Roll back to what the server last confirmed, or to unrated — and an unrated moment
            // is rateable again, unlocked even if the fan has scrolled away, so they can retry.
            const confirmed = confirmedRef.current[itemId];
            if (confirmed) {
              latestRef.current[itemId] = confirmed;
            } else {
              delete latestRef.current[itemId];
              setLockedIds((prev) => {
                if (!(itemId in prev)) return prev;
                const next = { ...prev };
                delete next[itemId];
                return next;
              });
            }
            setResults((prev) => {
              const next = { ...prev };
              if (confirmed) next[itemId] = confirmed;
              else delete next[itemId];
              return next;
            });
            setTallies((prev) => {
              const next = { ...prev };
              if (serverTalliesRef.current[itemId]) {
                next[itemId] = serverTalliesRef.current[itemId];
              } else {
                delete next[itemId];
              }
              return next;
            });
            dispatch({
              type: 'HANDLE_RES',
              payload: {
                err: t('tiri.rateIt.submitError', "Couldn't save your rating. Try again.")
              }
            });
            return;
          }

          confirmedRef.current[itemId] = res.rating || sent;
          // The server kept a different rating than this one — another change won a race, or the
          // fan rated this moment in the app before this page loaded. Its value wins; adopting it
          // rather than resending is also what stops an older server, which keeps the first
          // rating, from looping.
          if (res.rating && res.rating !== sent) {
            latestRef.current[itemId] = res.rating;
            setResults((prev) => ({ ...prev, [itemId]: res.rating }));
          }
          // The server's counts replace the optimistic move.
          if (res.tallies) {
            serverTalliesRef.current[itemId] = res.tallies;
            setTallies((prev) => ({ ...prev, [itemId]: res.tallies }));
          }

          /**
           * GATED ON A PURE RATING CYCLE. The every-5 cycle is shared with Tag It in the app, so
           * a fan can arrive at their fifth completion here having mostly tagged there — and
           * this modal says "You rated N moments!" and pitches inviting friends to rate. For a
           * mixed cycle it would misdescribe what they did, so it is skipped. An ABSENT breakdown
           * (an older server, or milestone tracking that threw) is read as "cannot tell" and
           * resolves to showing it, which is the behaviour that predates the field.
           */
          const pureRatingCycle = !res.milestoneBreakdown || res.milestoneBreakdown.tagit === 0;

          // Shown first when the milestone lands too — the render below holds the milestone back
          // until this is dismissed.
          if (res.referralCompleted) setReferralAward(res.referralCompleted);
          if (res.milestoneReached && pureRatingCycle) {
            setMilestone({ count: res.milestoneCount || RATE_IT_DEFAULT_MILESTONE_COUNT });
          }

          sent = latestRef.current[itemId];
        }
      } finally {
        pendingRef.current.delete(itemId);
      }
    },
    [dispatch, t, shortCode]
  );

  /**
   * A click on the bar. Allowed while the moment is unlocked, i.e. until the fan scrolls away
   * from it having rated it. A click on the tier already picked does nothing. A guest is sent to
   * sign-up, carrying the click so it can be replayed afterwards.
   *
   * Highlights, moves the counter and toasts optimistically in the click's own tick, then hands
   * off to `sendRating` — or, with a request for this item already in flight, just records the
   * new pick for that request to pick up when it lands.
   */
  const handleRate = useCallback(
    (itemId, rating) => {
      const previous = resultsRef.current[itemId];
      if (lockedIds[itemId] || previous === rating) return;

      if (!isSignedIn) {
        onRequireAuth({ itemId, rating });
        return;
      }

      ratedThisSessionRef.current.add(itemId);
      latestRef.current[itemId] = rating;
      setResults((prev) => ({ ...prev, [itemId]: rating }));
      setTallies((prev) => {
        const base =
          prev[itemId] ?? itemsRef.current.find((feedItem) => feedItem._id === itemId)?.tallies;
        // No counts on the item (an older server): nothing to move, and nothing shown.
        if (!base) return prev;
        const next = { ...base, [rating]: (base[rating] ?? 0) + 1 };
        // A change moves the vote: off the old tier as well as onto the new one.
        if (previous) next[previous] = Math.max(0, (base[previous] ?? 0) - 1);
        return { ...prev, [itemId]: next };
      });
      showToast(rating);

      // The first user gesture on the page. Browsers block unmuted autoplay until they see one,
      // so this is where the crowd noise the brief cares about can finally be turned on.
      setIsMuted(false);

      if (!pendingRef.current.has(itemId)) sendRating(itemId);
    },
    [lockedIds, isSignedIn, onRequireAuth, showToast, sendRating]
  );

  const handleIndexChange = useCallback(
    (index) => {
      // Leaving a rated moment locks it. Read before the index moves: the moment being left is
      // the one at the OLD index.
      const leftItem = itemsRef.current[currentIndexRef.current];
      if (index !== currentIndexRef.current && leftItem && resultsRef.current[leftItem._id]) {
        setLockedIds((prev) => (prev[leftItem._id] ? prev : { ...prev, [leftItem._id]: true }));
      }

      setCurrentIndex(index);
      if (index > 0) setBannerDismissed(true);

      if (
        index >= itemsRef.current.length - 1 &&
        hasNextPageRef.current &&
        !isFetchingRef.current
      ) {
        loadMoreRef.current();
      }
    },
    []
  );

  /**
   * Replays the tile a guest clicked before signing up.
   *
   * Fires once, guarded by a ref, and ONLY while the item is still in `items` — otherwise it is
   * dropped silently, which is the correct outcome: a rating must never be attributed to a
   * moment the fan is no longer looking at, and after an auth round trip the feed has been
   * redrawn at random.
   *
   * `handleRate` is deliberately not in the dependency list. It changes identity on every
   * `results` update, and re-running this effect for that reason could replay a second time;
   * the ref would stop it, but relying on the ref for correctness rather than the deps is the
   * bug that guard exists to hide. The ref is the belt, this is the braces.
   */
  const replayedRef = useRef(false);
  useEffect(() => {
    if (!pendingRating || !isSignedIn || replayedRef.current) return;

    const stillPresent = itemsRef.current.some((item) => item._id === pendingRating.itemId);
    replayedRef.current = true;
    if (stillPresent) handleRate(pendingRating.itemId, pendingRating.rating);
    onPendingRatingApplied?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRating, isSignedIn]);

  // The trailing skeleton, shown only while the next draw is in flight, so scrolling past the
  // last loaded moment lands on something rather than a dead end.
  const showSkeleton = hasNextPage && isFetchingNextPage;
  const slideCount = items.length + (showSkeleton ? 1 : 0);

  const renderSlide = (index) => {
    if (index >= items.length) return <RateItItemSkeleton />;

    const item = items[index];
    const distance = Math.abs(index - currentIndex);

    return (
      <>
        <RateItItemCard
          item={item}
          viewCount={
            Math.max(item.viewCount ?? 0, serverViews[item._id] ?? 0) + (localViews[item._id] ?? 0)
          }
          isActive={index === currentIndex}
          // Only the current item ±1 mounts a player. The slide itself always renders — removing
          // it would collapse the scroll container's height and break snapping.
          isMounted={distance <= 1}
          isMuted={isMuted}
          // Every mounted card, not just the active one: the neighbours buffer, so without this
          // one of them could start playing under the modal. A modal pauses too, so a clip can't
          // end behind the celebration and advance the fan off the moment that earned it.
          isPaused={isPaused || isModalOpen}
          onTogglePause={() => setIsPaused((paused) => !paused)}
          selectedRating={results[item._id] ?? null}
          tallies={tallies[item._id]}
          // Locked once the fan has scrolled away from a moment they rated. Until then the pick
          // can change, even with a request in flight — see `sendRating`.
          ratingLocked={!!lockedIds[item._id]}
          onRate={handleRate}
          onBack={onBack}
          // Active card only: a buffering neighbour never plays, but don't let it advance us. Not
          // once rated either — moving on would lock the pick without the fan leaving.
          onVideoEnd={index === currentIndex && !results[item._id] ? advanceIdle : undefined}
        />

        {/* Over the first card ONLY — it introduces the shared moment, and by the second it
            would describe something the fan is no longer looking at.

            Rendered INSIDE the slide rather than over the feed's root, which is the full
            viewport: above the desktop breakpoint the moment is a centred 9:16 stage, and a
            banner anchored to the root ran the full window width, crossing outside the moment
            it belongs to. */}
        {index === 0 && referral && !bannerDismissed ? (
          <RateItReferralBanner
            sender={referral.sender}
            points={referral.points}
            isLoading={referral.isLoading}
          />
        ) : null}
      </>
    );
  };

  return (
    <Root>
      <RateItPager
        ref={pagerRef}
        count={slideCount}
        currentIndex={currentIndex}
        onIndexChange={handleIndexChange}
        renderSlide={renderSlide}
      />

      {toastRating ? <RateItRatingToast rating={toastRating} /> : null}

      {referralAward ? (
        <ReferralAwardModal
          bonusPoints={referralAward.points}
          body={
            referralAward.referrerName
              ? t('tiri.rateIt.referral.awardBody', {
                  name: referralAward.referrerName,
                  points: referralAward.points
                })
              : t('tiri.rateIt.referral.awardBodyNoSender', { points: referralAward.points })
          }
          onContinue={() => {
            setReferralAward(null);
            // The invite is finished, so its "rate N and you both earn" offer is spent.
            setBannerDismissed(true);
          }}
        />
      ) : milestone ? (
        <RateItMilestoneModal count={milestone.count} onClose={() => setMilestone(null)} />
      ) : null}
    </Root>
  );
};

export default RateItFeed;
